import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  useGetCustomerFieldsQuery,
  useCreateCustomerFieldMutation,
  CustomerFieldsFilters,
} from "../../api/customerFieldApi";
import { CustomerFieldCard } from "./CustomerFieldCard";
import { Button } from "../../components/tailwind/button/Button";
import { InriverSyncModal } from "./InriverSyncModal";
import {
  CustomerFieldFilters,
  CustomerFieldFiltersState,
} from "./CustomerFieldFilters";

// Helper to parse comma-separated URL param to array
const parseArrayParam = (value: string | null): string[] => {
  if (!value) return [];
  return value.split(",").filter((v) => v.trim() !== "");
};

// Helper to serialize array to comma-separated URL param
const serializeArrayParam = (arr: string[]): string | null => {
  if (arr.length === 0) return null;
  return arr.join(",");
};

// Custom hook for URL state with array support
const useFilterUrlState = (): {
  filters: CustomerFieldFiltersState;
  setFilters: (filters: CustomerFieldFiltersState) => void;
  settled: boolean;
} => {
  const [settled, setSettled] = useState(false);
  const [filters, setFiltersState] = useState<CustomerFieldFiltersState>({
    search: "",
    entityIds: [],
    types: [],
  });

  // Initialize from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const search = params.get("search") || "";
    const entityIds = parseArrayParam(params.get("entity_ids"));
    const types = parseArrayParam(params.get("types"));
    setFiltersState({ search, entityIds, types });
    setSettled(true);
  }, []);

  // Update URL when filters change
  const setFilters = useCallback((newFilters: CustomerFieldFiltersState) => {
    setFiltersState(newFilters);

    const params = new URLSearchParams(window.location.search);

    // Preserve unrelated params
    const preservedParams = ["jwt", "autologin", "xid", "bucket"];
    const preserved: [string, string][] = [];
    preservedParams.forEach((key) => {
      const value = params.get(key);
      if (value) preserved.push([key, value]);
    });

    // Build new params
    const newParams = new URLSearchParams();
    preserved.forEach(([key, value]) => newParams.set(key, value));

    if (newFilters.search) {
      newParams.set("search", newFilters.search);
    }
    const entityIdsParam = serializeArrayParam(newFilters.entityIds);
    if (entityIdsParam) {
      newParams.set("entity_ids", entityIdsParam);
    }
    const typesParam = serializeArrayParam(newFilters.types);
    if (typesParam) {
      newParams.set("types", typesParam);
    }

    const queryString = newParams.toString();
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}${queryString ? `?${queryString}` : ""}`
    );
  }, []);

  return { filters, setFilters, settled };
};

export const CustomerFieldsPage: React.FC = () => {
  const { filters, setFilters, settled } = useFilterUrlState();

  // Convert filter state to API format
  const apiFilters: CustomerFieldsFilters = useMemo(
    () => ({
      search: filters.search || undefined,
      entity_ids: filters.entityIds.length > 0 ? filters.entityIds : undefined,
      types: filters.types.length > 0 ? filters.types : undefined,
    }),
    [filters]
  );

  const { data, isLoading, error, isFetching } = useGetCustomerFieldsQuery(
    settled ? apiFilters : undefined,
    {
      skip: !settled,
    }
  );
  const [
    createField,
    { isLoading: isCreating },
  ] = useCreateCustomerFieldMutation();

  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newFieldId, setNewFieldId] = useState("");
  const [showInriverModal, setShowInriverModal] = useState(false);

  const handleCreateField = async (): Promise<void> => {
    if (!newFieldId.trim()) return;

    try {
      await createField({ field_id: newFieldId.trim() }).unwrap();
      setNewFieldId("");
      setIsAddingNew(false);
    } catch (err) {
      console.error("Failed to create field:", err);
    }
  };

  const handleCancelAdd = (): void => {
    setNewFieldId("");
    setIsAddingNew(false);
  };

  if (isLoading) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-py-12">
        <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-4 tw-text-red-700">
        Failed to load customer fields. Please try again.
      </div>
    );
  }

  const fields = data?.fields || [];
  const fieldTypeChoices = data?.field_type_choices || [];
  const canSyncFromInriver = data?.can_sync_from_inriver || false;
  const hasActiveFilters =
    filters.search.trim() !== "" ||
    filters.entityIds.length > 0 ||
    filters.types.length > 0;

  return (
    <div className="tw-w-full tw-space-y-6">
      <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
        <h1 className="tw-text-2xl tw-font-semibold tw-text-gray-900">
          PIM Fields
        </h1>
        {!isAddingNew && (
          <div className="tw-flex tw-gap-2">
            {canSyncFromInriver && (
              <Button
                variant="secondary-alt"
                icon
                data-testid="import-from-inriver-button"
                onClick={() => setShowInriverModal(true)}
              >
                <i className="material-icons">sync</i>
                Import from InRiver
              </Button>
            )}
            <Button
              variant="primary-alt"
              icon
              onClick={() => setIsAddingNew(true)}
            >
              <i className="material-icons">add</i>
              Add field
            </Button>
          </div>
        )}
      </div>

      {showInriverModal && (
        <InriverSyncModal onClose={() => setShowInriverModal(false)} />
      )}

      <CustomerFieldFilters
        filters={filters}
        onFiltersChange={setFilters}
        fieldTypeChoices={fieldTypeChoices}
        isFiltering={isFetching && !isLoading}
      />

      {isAddingNew && (
        <div className="tw-bg-white tw-rounded-lg tw-shadow tw-border tw-border-gray-200 tw-p-4">
          <h3 className="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-4">
            Add New Field
          </h3>
          <div className="tw-flex tw-gap-3 tw-items-end">
            <div className="tw-flex-1">
              <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                Field ID
              </label>
              <input
                type="text"
                value={newFieldId}
                onChange={(e) => setNewFieldId(e.target.value)}
                placeholder="Enter field ID..."
                className="tw-block tw-w-full tw-rounded-md tw-border-gray-300 tw-shadow-sm focus:tw-border-blue-500 focus:tw-ring-blue-500 tw-text-sm"
                autoFocus
              />
            </div>
            <Button
              variant="primary"
              onClick={handleCreateField}
              disabled={isCreating || !newFieldId.trim()}
            >
              {isCreating ? "Creating..." : "Create"}
            </Button>
            <Button variant="secondary" onClick={handleCancelAdd}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {fields.length === 0 ? (
        <div
          className="tw-bg-gray-50 tw-rounded-lg tw-p-8 tw-text-center"
          data-testid="no-filter-match-div"
        >
          <div className="tw-text-gray-500 tw-mb-4">
            <i className="material-icons tw-text-4xl">
              {hasActiveFilters ? "search_off" : "inventory_2"}
            </i>
          </div>
          <h3 className="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-2">
            {hasActiveFilters
              ? "No fields match your filters"
              : "No fields yet"}
          </h3>
          <p className="tw-text-gray-500 tw-mb-4">
            {hasActiveFilters
              ? "Try adjusting your search or filter criteria."
              : "Add customer fields to configure how data is processed."}
          </p>
          {!isAddingNew && !hasActiveFilters && (
            <Button variant="primary" onClick={() => setIsAddingNew(true)}>
              Add Your First Field
            </Button>
          )}
          {hasActiveFilters && (
            <Button
              variant="secondary"
              onClick={() =>
                setFilters({ search: "", entityIds: [], types: [] })
              }
            >
              Clear filters
            </Button>
          )}
        </div>
      ) : (
        <div className="tw-w-full tw-space-y-3">
          {fields.map((field) => (
            <CustomerFieldCard
              key={field.id}
              field={field}
              fieldTypeChoices={fieldTypeChoices}
            />
          ))}
        </div>
      )}
    </div>
  );
};
